/**
 * The frame every signed-in route renders inside.
 *
 * Carries the skip link, which exists because the sidebar is the first thing
 * in the tab order on every page. Without it a keyboard user tabs through
 * every nav item before reaching the content, on every navigation.
 */

import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { visibleNav, activeNavPath } from './navigation';
import { useVisible as useOrgVisibility } from '../features/org/useVisibility';
import { NavIcon } from './NavIcon';
import { NO_ADDONS, useAddons } from '../features/org/useAddons';
import { useOrgHealth } from '../features/org/useOrgHealth';
import { SsoHealthBanner } from './SsoHealthBanner';
import { hasAtLeast } from '../types/roles';
import { ROLE_DISPLAY_NAMES } from '../types/roles';
import { HelpButton } from '../features/help/HelpButton';
import styles from './AppShell.module.css';
import { useLoadableApprovals } from '../features/approvals/approvalsData';
import { useRoleSync } from '../auth/useRoleSync';
import { Wordmark } from '../shared/Wordmark';

export function AppShell() {
  /**
   * The rules read roles from the token; the callables read them live. This
   * pulls a fresh token the moment an administrator changes this person's
   * roles, instead of leaving the two disagreeing for up to an hour.
   */
  useRoleSync();
  const { session, signOut } = useAuth();
  const roles = session?.roles ?? [];
  // Until the organization document has been read, add-on items are simply
  // not there yet; they appear once it is. Better than a flash of an item
  // that then disappears.
  const addons = useAddons(session?.orgId ?? null);
  const health = useOrgHealth(session?.orgId ?? null);
  /**
   * Below Reviewer the sidebar has no Approvals item — but an administrator
   * may name any active member on a step, and the server lets that person
   * decide it. So for exactly those people we ask whether anything is waiting.
   * The callable needs only `viewer` and returns nothing they may not decide,
   * so this grants no access; it reveals work they already had. Reviewers and
   * above already see the item, so they are not asked and pay nothing.
   */
  const named = useLoadableApprovals(Boolean(session) && !hasAtLeast(roles, 'reviewer'));
  const orgVisible = useOrgVisibility();
  const items = visibleNav(roles, addons.data ?? NO_ADDONS, (named.data?.instances ?? []).length > 0, orgVisible.acknowledgementReports);
  const { pathname } = useLocation();
  // One owner per path, decided in one place — see activeNavPath.
  const active = activeNavPath(pathname, items);

  const roleLabel = roles.length
    ? roles.map((r) => ROLE_DISPLAY_NAMES[r]).join(', ')
    : 'No role assigned';

  return (
    <div className={styles.shell}>
      <a href="#main" className={styles.skip}>
        Skip to content
      </a>

      <aside className={styles.side}>
        <div className={styles.brand}>
          <Wordmark withIcon className={styles.brandMark} />
        </div>

        <nav aria-label="Main">
          <ul className={styles.nav}>
            {items.map((item) => (
              <li key={item.to}>
                {/*
                  A plain Link, not NavLink. NavLink decides "active" itself by
                  prefix and sets both the class and aria-current from it —
                  which is what lit up Policies on /policies/upload. Passing our
                  own aria-current alongside left two sources of truth and
                  NavLink still winning. One owner per path, decided by
                  activeNavPath, and nothing else gets a vote.
                */}
                <Link
                  to={item.to}
                  className={
                    active === item.to ? `${styles.link} ${styles.linkActive}` : styles.link
                  }
                  aria-current={active === item.to ? 'page' : undefined}
                >
                  <span className={styles.linkMain}>
                    <NavIcon name={item.icon} className={styles.linkIcon} />
                    <span>{item.label}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.sideFoot}>
          <NavLink to="/settings" className={styles.whoLink}>
            <b>{session?.displayName ?? session?.email ?? 'Signed in'}</b>
          </NavLink>
          <div className={styles.roleList}>{roleLabel}</div>
          <button type="button" className={styles.signOut} onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <div className={styles.main}>
        <div className={styles.topbar}>
          <HelpButton />
        </div>
        <main id="main" tabIndex={-1}>
          {hasAtLeast(session?.roles ?? [], 'admin') && <SsoHealthBanner health={health.data} />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
